/*
Vehicle-type typing of the travel-cost matrix.

A travel-cost row is TYPED when it carries a `vehicleTypeId`. The solver treats
that as a SWITCH over the whole matrix, not a per-row test: once any row carries
a vehicleTypeId, only typed rows are read, and every ordered pair needs one for
the vehicle's type or the run fails outright (a pandas KeyError, not a partial
plan). A matrix that mixes typed and untyped rows therefore either fails, or
"succeeds" with the stops whose rows were ignored left unassigned.

The front end used to decide whether to WRITE typed rows from a display flag —
`tableSettings.travel_costs.isViewable.vehicleType` — rather than from the rows
themselves. Three published examples (lab-servicing-vehicle-types,
portland-recycling-split-body, miami-executive-airport-transfer) ship a fully
typed matrix with that flag false, so adding one stop to any of them wrote a
single untyped row per new pair beside thousands of typed ones: the failing
shape above, one click from the gallery.

This module is the one place that answers "is this matrix typed?", and the
boundaries (load, upload, populate, send) put rows through it so the flag and
the data can never contradict each other again.

Everything here is O(n) in rows and allocates nothing when there is nothing to
repair: these matrices reach 23k rows (portland-recycling-split-body).
*/

import { generateUUID } from 'store/functions/geoJsonFunctions.js';

/** A row is typed when it carries a vehicle type id. '' is not an id. */
export const rowIsTyped = (row) =>
  row != null && row.vehicleTypeId != null && row.vehicleTypeId !== '';

const rowHasTypeName = (row) =>
  row != null && row.vehicleType != null && row.vehicleType !== '';

/**
 * Does the solver read this matrix as typed?
 *
 * Mirrors the backend switch: ANY typed row (self-pairs included) makes the
 * whole matrix typed. Short-circuits, so a typed table answers on its first row.
 */
export const travelCostsAreTyped = (travelCosts) =>
  Array.isArray(travelCosts) && travelCosts.some(rowIsTyped);

/**
 * Name <-> id maps for the vehicle types in play.
 *
 * A name shared by two vehicle types resolves to neither: guessing which one a
 * row meant would silently send half the matrix to the wrong fleet. Ambiguous
 * names are dropped from the name->id direction only — id->name stays exact.
 */
const buildVehicleTypeMaps = (vehicleTypes) => {
  const idByName = new Map();
  const ambiguousNames = new Set();
  const nameById = new Map();
  for (const vehicleType of vehicleTypes) {
    if (vehicleType == null) continue;
    const id = vehicleType.UUID ?? vehicleType.vehicleTypeId ?? null;
    const name = vehicleType.vehicleType ?? null;
    if (id == null || id === '' || name == null || name === '') continue;
    if (!nameById.has(id)) nameById.set(id, name);
    if (idByName.has(name) && idByName.get(name) !== id) {
      ambiguousNames.add(name);
    } else {
      idByName.set(name, id);
    }
  }
  for (const name of ambiguousNames) idByName.delete(name);
  return { idByName, nameById };
};

/** A location's row to itself. The backend generates these, so they are treated
 *  differently throughout: exempt from the all-or-nothing typing invariant, and
 *  never counted towards a notice — we don't tell people we repaired a row we
 *  were handed. (Tightens once the backend emits typed self-pairs; see
 *  utility_functions.py ~149.) */
const isSelfPair = (row) =>
  row != null && row.fromLocationId != null && row.fromLocationId === row.toLocationId;

const pairKeyOf = (row) => `${row.fromLocationId}\u0000${row.toLocationId}`;

/**
 * Put a travel-cost table into a shape the solver can actually read.
 *
 * Three repairs, counted separately because they mean different things:
 *
 *  - `idsFilled` — a row named a vehicle type but wasn't linked to it. Every row
 *    written before 2026-09-15, every row that survived an upload, and every row
 *    the old add-location path wrote is this shape. An unlinked row is invisible
 *    to the solver as soon as anything else is linked.
 *  - `namesFilled` — a row carried the id but no name (the published examples are
 *    exactly this). Display only; the solver never reads the name.
 *  - `rowsExpanded` — the case linking alone would make WORSE. A matrix holding
 *    both named rows and bare ones (an old scenario, plus stops added by the
 *    build this fixes) reads as untyped today: the solver applies every row to
 *    every vehicle, and with more than one vehicle type it sees the same pair
 *    repeatedly. Linking the named rows flips the matrix to typed, and the bare
 *    rows — real pairs, written to serve all types — would drop out of the
 *    solve. So each bare off-diagonal row is replaced by a copy per vehicle type
 *    that has no row for its pair yet, which is what "a row for all types"
 *    always meant. Values are preserved; no pair loses its coverage and none
 *    gains a duplicate.
 *
 * Self-pairs are left exactly as they are: the backend adds untyped ones of its
 * own, and rewriting those would be us reporting on its work.
 *
 * Returns the ORIGINAL array when nothing changed, so callers memoizing on the
 * table reference (getCostIndex, selectors) don't rebuild for a no-op.
 * Single pass to classify, one more only when there is something to expand.
 *
 * @returns {{rows: Array, idsFilled: number, namesFilled: number,
 *            idsFilledOffDiagonal: number, rowsExpanded: number}}
 */
export const normalizeTravelCostRows = (travelCosts, vehicleTypes) => {
  const unchanged = {
    rows: travelCosts, idsFilled: 0, namesFilled: 0, idsFilledOffDiagonal: 0, rowsExpanded: 0,
  };
  if (!Array.isArray(travelCosts) || travelCosts.length === 0) return unchanged;
  if (!Array.isArray(vehicleTypes) || vehicleTypes.length === 0) return unchanged;

  const { idByName, nameById } = buildVehicleTypeMaps(vehicleTypes);
  if (idByName.size === 0 && nameById.size === 0) return unchanged;

  let idsFilled = 0;
  let idsFilledOffDiagonal = 0;
  let namesFilled = 0;
  let typed = false;
  let out = null;
  // Off-diagonal rows still untyped after linking, and which types each pair
  // already has — both only needed if the table turns out to be typed.
  const untypedIdx = [];
  const typesByPair = new Map();

  for (let i = 0; i < travelCosts.length; i++) {
    const row = travelCosts[i];
    if (row == null) continue;
    const hasName = rowHasTypeName(row);
    let next = row;
    if (!rowIsTyped(row) && hasName) {
      const id = idByName.get(row.vehicleType);
      if (id != null) {
        next = { ...row, vehicleTypeId: id };
        idsFilled += 1;
        if (!isSelfPair(row)) idsFilledOffDiagonal += 1;
      }
    } else if (rowIsTyped(row) && !hasName) {
      const name = nameById.get(row.vehicleTypeId);
      if (name != null) {
        next = { ...row, vehicleType: name };
        namesFilled += 1;
      }
    }
    if (next !== row) {
      if (out === null) out = travelCosts.slice();
      out[i] = next;
    }
    if (rowIsTyped(next)) {
      typed = true;
      if (next.fromLocationId != null && next.toLocationId != null) {
        const key = pairKeyOf(next);
        let types = typesByPair.get(key);
        if (!types) { types = new Set(); typesByPair.set(key, types); }
        types.add(next.vehicleTypeId);
      }
    } else if (!isSelfPair(next) && next.fromLocationId != null && next.toLocationId != null) {
      untypedIdx.push(i);
    }
  }

  let rowsExpanded = 0;
  if (typed && untypedIdx.length > 0) {
    const source = out || travelCosts;
    const drop = new Set(untypedIdx);
    const expanded = [];
    for (const i of untypedIdx) {
      const row = source[i];
      const have = typesByPair.get(pairKeyOf(row));
      for (const vehicleType of vehicleTypes) {
        if (vehicleType == null) continue;
        const id = vehicleType.UUID ?? vehicleType.vehicleTypeId ?? null;
        if (id == null || id === '' || (have && have.has(id))) continue;
        // Its own UUID. Copies sharing one are a different bug wearing this
        // one's clothes: deleteWhere removes every row with that id, and
        // existsInTable/getRowAt always answer with the first — so deleting one
        // type's row would take the others' with it, and editing would edit the
        // wrong row.
        expanded.push({
          ...row,
          UUID: generateUUID(),
          vehicleTypeId: id,
          vehicleType: vehicleType.vehicleType ?? null,
        });
      }
    }
    rowsExpanded = expanded.length;
    out = source.filter((_, i) => !drop.has(i)).concat(expanded);
  }

  if (out === null) return unchanged;
  return { rows: out, idsFilled, namesFilled, idsFilledOffDiagonal, rowsExpanded };
};

/**
 * The plain notice shown when rows had to be matched up to their vehicle types.
 *
 * Only repairs the person could not have expected are announced, and only ones
 * that change what the solver reads: back-filling a display name is silent, and
 * so is anything done to a self-pair row, which the backend writes itself.
 */
export const travelCostsRelinkedMessage = ({ relinked = 0, expanded = 0 } = {}) => {
  const rows = (n) => `${n} travel cost ${n === 1 ? 'row' : 'rows'}`;
  const consequence = 'Left as they were, the solver would have skipped those rows, and your run could have failed or left stops unassigned.';
  if (relinked > 0 && expanded > 0) {
    return `Your travel costs didn't all say which vehicle type they were for, so we matched them up: ${rows(relinked)} linked by name, and ${expanded} added so every vehicle type has its own. ${consequence}`;
  }
  if (relinked > 0) {
    return `${rows(relinked)} named a vehicle type but weren't linked to one, so we matched them up by name. ${consequence}`;
  }
  return `Some travel costs didn't say which vehicle type they were for, so we copied them across your vehicle types — ${expanded} ${expanded === 1 ? 'row' : 'rows'} added. ${consequence}`;
};

/**
 * Is there an unlinked row a solve would drop? Cheap, read-only, and safe to
 * call on the frozen store state before deciding to dispatch a repair — which
 * keeps the expensive draft out of the common (nothing-to-do) case.
 */
export const travelCostTypingNeedsRepair = (state) => {
  const data = state && state.data;
  if (!data || !Array.isArray(data.travel_costs) || data.travel_costs.length === 0) return false;
  if (!Array.isArray(data.vehicle_types) || data.vehicle_types.length === 0) return false;
  let typed = false;
  let bareOffDiagonal = false;
  for (const row of data.travel_costs) {
    if (row == null) continue;
    if (!rowIsTyped(row) && rowHasTypeName(row)) return true;
    if (rowIsTyped(row)) typed = true;
    else if (!isSelfPair(row) && row.fromLocationId != null && row.toLocationId != null) {
      bareOffDiagonal = true;
    }
    if (typed && bareOffDiagonal) return true;
  }
  return false;
};

/**
 * Bring a state's travel costs and their display flag into agreement.
 *
 * Mutates `state` in place, so it works both on a plain state object (the
 * scenario initializer builds one) and on an immer draft (reducers).
 *
 * The flag follows the rows, never the other way round — that inversion is the
 * original bug. An empty matrix says nothing about typing, so the flag is left
 * alone there (a person who turned on vehicle-type dependence before adding any
 * locations keeps their choice).
 *
 * @param {Object} state - a scenario state ({data, tableSettings, notifications}).
 * @param {Object} [options]
 * @param {boolean} [options.notify] - push one plain notice when rows were relinked.
 * @param {Array} [options.vehicleTypes] - types to match against, when they are not
 *   (yet) the ones in `state.data.vehicle_types`. The upload path needs this: the
 *   types a file introduces are added to the table only after its rows are built.
 * @returns {{idsFilled: number, namesFilled: number, typed: boolean}}
 */
export const reconcileTravelCostTyping = (state, { notify = false, vehicleTypes = null } = {}) => {
  const data = state && state.data;
  if (!data || !Array.isArray(data.travel_costs)) {
    return { idsFilled: 0, namesFilled: 0, typed: false };
  }

  const { rows, idsFilled, namesFilled, idsFilledOffDiagonal, rowsExpanded } =
    normalizeTravelCostRows(data.travel_costs, vehicleTypes || data.vehicle_types);
  if (rows !== data.travel_costs) data.travel_costs = rows;

  const typed = travelCostsAreTyped(rows);
  const isViewable = state.tableSettings
    && state.tableSettings.travel_costs
    && state.tableSettings.travel_costs.isViewable;
  if (isViewable && rows.length > 0 && isViewable.vehicleType !== typed) {
    isViewable.vehicleType = typed;
  }

  const announceable = idsFilledOffDiagonal + rowsExpanded;
  if (notify && announceable > 0 && state.notifications && Array.isArray(state.notifications.routine)) {
    state.notifications.routine.push({
      type: 'info',
      message: travelCostsRelinkedMessage({ relinked: idsFilledOffDiagonal, expanded: rowsExpanded }),
    });
  }

  return { idsFilled, namesFilled, idsFilledOffDiagonal, rowsExpanded, typed };
};

/**
 * Repair the travel costs inside a PERSISTED blob, for the redux-persist
 * migration. Mirrors forgetPersistedScenarioIds: it fixes each snapshot in the
 * stored object and returns a new one, touching nothing else.
 *
 * Rehydrating does not go through `initializeData`, so nothing else on that
 * path ever looks at these rows. A browser carrying a name-only matrix from
 * before 2026-09-15 therefore kept it. Every such row's merge key ends in its
 * type NAME while the rows coming back from /cost_matrix end in its id. The
 * merge in keepExistingTravelCosts then matches nothing, and "Calculate
 * missing" quietly replaced values people had typed — the one outcome that
 * change exists to prevent.
 *
 * Silent, like every migration step: this runs before the first paint, so the
 * table someone sees is the repaired one. The solve and populate boundaries
 * still speak up for anything that reaches them repairable.
 *
 * @param {object} persisted - the stored inputData slice ({present, _latestUnfiltered, ...}).
 * @returns {object}
 */
export const repairPersistedTravelCosts = (persisted) => {
  if (!persisted || typeof persisted !== 'object') return persisted;
  const fix = (snapshot) => {
    if (!snapshot || typeof snapshot !== 'object' || !snapshot.data) return snapshot;
    const { rows } = normalizeTravelCostRows(snapshot.data.travel_costs, snapshot.data.vehicle_types);
    if (rows === snapshot.data.travel_costs) return snapshot;
    const next = { ...snapshot, data: { ...snapshot.data, travel_costs: rows } };
    // The display flag follows the rows here too, so a rehydrated scenario
    // cannot come back with the two disagreeing.
    const isViewable = next.tableSettings
      && next.tableSettings.travel_costs
      && next.tableSettings.travel_costs.isViewable;
    if (isViewable && rows.length > 0) {
      next.tableSettings = {
        ...next.tableSettings,
        travel_costs: {
          ...next.tableSettings.travel_costs,
          isViewable: { ...isViewable, vehicleType: travelCostsAreTyped(rows) },
        },
      };
    }
    return next;
  };
  const next = { ...persisted };
  if ('present' in next) next.present = fix(next.present);
  if ('_latestUnfiltered' in next) next._latestUnfiltered = fix(next._latestUnfiltered);
  return next;
};

/**
 * Which vehicle types must a newly created travel-cost row be written for?
 *
 * `null` means "write one untyped row", an array means "write one row per
 * vehicle type, carrying both the id and the name".
 *
 * The DATA decides. Rows already in the table are the authority, because they
 * are what the solver will be reading alongside the new ones — matching them is
 * the whole point. Only when there are no rows to learn from does the scenario's
 * intent (the vehicle-type column being shown, or vehicle-type-dependent costs)
 * get a say. With no vehicle types at all there is nothing to type rows by,
 * which is the guard `travelCostUpdate` always had and this path lacked.
 */
export const vehicleTypesForNewCostRows = (state) => {
  const data = state && state.data;
  const vehicleTypes = data && data.vehicle_types;
  if (!Array.isArray(vehicleTypes) || vehicleTypes.length === 0) return null;

  const rows = data.travel_costs;
  if (Array.isArray(rows) && rows.length > 0) {
    return travelCostsAreTyped(rows) ? vehicleTypes : null;
  }

  const isViewable = state.tableSettings
    && state.tableSettings.travel_costs
    && state.tableSettings.travel_costs.isViewable;
  const dependence = state.generalSettings
    && state.generalSettings.costs
    && state.generalSettings.costs.vehicleTypeDependence;
  return (isViewable && isViewable.vehicleType) || dependence ? vehicleTypes : null;
};
